import React, { useState, useEffect, useMemo, useRef } from 'react';

import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Target,
  Activity,
  Image,
  Calendar,
  Mail,
  Users,
  Eye,
  MousePointer,
  TrendingUp,
  Settings,
  Copy,
  Check,
  ExternalLink,
  Monitor,
  CheckCircle,
  Files,
  FileCheck2,
  GalleryVerticalEnd,
  Building2
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { AdvertisementCategory, AdvertisementPriority, PromocodeType, Entity } from '../types';

import { pbClickTracker } from '../services/pocketbaseClickTracking';
import { PartnerCampaignManager } from './PartnerCampaignManager';
import { PromoRequestsManager } from './PromoRequestsManager';
import { ActivityLogsManager } from './ActivityLogsManager';
import { getCountryNames } from '../data/countries';
import { getPartnerLogoUrl } from '../utils/pocketbaseHelpers';
import { getLocaleFromRegion } from '../utils/translations';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';
import { getBillingReport, BillingReportPreset, BillingReportResult } from '../services/billingReportService';
import { EmailNotificationJobsManager } from './EmailNotificationJobsManager';
import { ApiKeysConfig } from './ApiKeysConfig';
import { WebhooksConfig } from './WebhooksConfig';
import { debugLogPartner } from '../services/debugConfig';
import { EntitiesManager } from './EntitiesManager';

const PROMOCODE_TYPE_STYLES: Record<PromocodeType | 'default', { container: string; label: string; note: string }> = {
  generic_static: {
    container: 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200',
    label: 'text-green-700',
    note: 'text-green-600'
  },
  generic_pool: {
    container: 'bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200',
    label: 'text-teal-700',
    note: 'text-teal-600'
  },
  personalized_generated: {
    container: 'bg-gradient-to-r from-purple-50 to-fuchsia-50 border border-purple-200',
    label: 'text-purple-700',
    note: 'text-purple-600'
  },
  byop: {
    container: 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200',
    label: 'text-yellow-800',
    note: 'text-yellow-600'
  },
  redirect: {
    container: 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200',
    label: 'text-blue-700',
    note: 'text-blue-600'
  },
  none: {
    container: 'bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200',
    label: 'text-gray-700',
    note: 'text-gray-500'
  },
  default: {
    container: 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200',
    label: 'text-blue-700',
    note: 'text-blue-600'
  }
};

const DESCRIPTION_MAX_LENGTH = 1000;

export const PartnerAdminDashboard: React.FC = () => {
  const { 
    user, 
    advertisements, 
    partnerCampaigns,
    marketingCampaigns,
    promoCodeRequests,
    partners,
    categories,
    filters,
    createAdvertisement,
    updateAdvertisement,
    deleteAdvertisement
  } = useAuth();
  
  // Early return if user is not loaded
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }
  
  const [activeTab, setActiveTab] = useState<'overview' | 'advertisements' | 'campaigns' | 'promo-requests' | 'reporting' | 'integrations' | 'entities' | 'logs'>(() => {
    // Restore active tab from sessionStorage after reload
    const savedTab = sessionStorage.getItem('partnerAdminActiveTab');
    return (savedTab as any) || 'overview';
  });

  const partnerDebugId = user.partner_id || 'unknown_partner';

  // Save active tab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('partnerAdminActiveTab', activeTab);
  }, [activeTab]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAd, setEditingAd] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [dateError, setDateError] = useState<string>('');
  const [showMagicLinkDropdown, setShowMagicLinkDropdown] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);

  // Helper function to get default dates
  const getDefaultDates = () => {
    try {
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30); // 30 days from today
      
      return {
        start_date: today.toISOString().split('T')[0], // Format: YYYY-MM-DD
        end_date: endDate.toISOString().split('T')[0]
      };
    } catch (error) {
      console.error('Error creating default dates:', error);
      // Fallback dates
      return {
        start_date: '2024-01-01',
        end_date: '2024-02-01'
      };
    }
  };

  const defaultDates = getDefaultDates();
  
  const [adForm, setAdForm] = useState({
    name: '',
    description: '',
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
    banner_url: '',
    banner_file: null as File | null,
    status: 'active' as any,
    priority: 'bronze' as AdvertisementPriority,
    advertisement_mode: 'default' as 'default' | 'independent' | 'universal',
    categories: [] as AdvertisementCategory[],
    region: '', // Selected region for the advertisement
    button_text: '', // Customizable button text
    badge_text: '', // Optional badge text
    badge_color: 'red' as 'red' | 'blue' | 'green' | 'yellow' | 'purple' | 'orange',
    promocode_delivery_methods: {
      web_display: true,
      email: false
    },
    target_url: '', // Optional URL to partner's page where user can use the promocode
    target_url_enabled: false, // Whether to show target URL link after promo request
    // Provider information - defaults to partner info
    provider_entity_id: '' as string, // Entity ID for provider info
    provider_name: '', // Custom provider name
    provider_logo_url: '', // Custom provider logo URL
    provider_privacy_policy_url: '', // Custom provider privacy policy URL
    promocode_config: {
      type: 'none' as any,
      static_code: '',
      pool_codes: [] as string[],
      pool_usage_limit: 1,
      used_codes: [] as string[],
      redirect_url: '',
      generation_rules: {
        length: 8,
        include_numbers: true,
        include_special_chars: false,
        prefix: '',
        suffix: ''
      },
      api_config: {
        endpoint: '',
        api_key: '',
        method: 'POST' as 'GET' | 'POST',
        headers: {} as Record<string, string>,
        response_code_path: '',
        timeout_seconds: 15,
        fallback: {
          enabled: false,
          type: 'static' as 'static' | 'dynamic',
          static_code: '',
          generation_rules: {
            length: 10,
            prefix: '',
            include_numbers: true,
            include_special_chars: false
          }
        }
      }
    }
  });

  const [clickMetrics, setClickMetrics] = useState({
    totalClicks: 0,
    totalViews: 0,
    uniqueSessions: 0,
    conversionRate: 0,
    publicLinkClicks: 0,
    promoRequests: 0,
    totalDisplays: 0
  });
  
  const [allPartnersAnalytics, setAllPartnersAnalytics] = useState<Array<{partnerId: string, publicLinkClicks: number}>>([]);
  const [promocodeConfigs, setPromocodeConfigs] = useState<Record<string, any>>({});
  const [partnerEntities, setPartnerEntities] = useState<Entity[]>([]);

  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);

  const handleDescriptionChange = (value: string) => {
    setAdForm(prev => ({
      ...prev,
      description: value.slice(0, DESCRIPTION_MAX_LENGTH)
    }));
  };

  // ASCII-only validation for promocodes (blocks non-ASCII characters like Cyrillic)
  const sanitizeToAscii = (value: string): string => {
    // Only allow printable ASCII characters (codes 32-126)
    return value.replace(/[^\x20-\x7E]/g, '');
  };

  const isAsciiOnly = (value: string): boolean => {
    return /^[\x20-\x7E]*$/.test(value);
  };

  const applyFormatting = (format: 'bold' | 'italic' | 'underline' | 'bullet' | 'number') => {
    const textarea = descriptionRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd, value } = textarea;
    const selectedText = value.slice(selectionStart, selectionEnd);
    let replacement = selectedText;
    let cursorPosition = selectionStart + replacement.length;

    switch (format) {
      case 'bold': {
        replacement = `**${selectedText}**`;
        if (!selectedText) {
          cursorPosition = selectionStart + 2; // place cursor between **|
        }
        break;
      }
      case 'italic': {
        replacement = `_${selectedText}_`;
        if (!selectedText) {
          cursorPosition = selectionStart + 1;
        }
        break;
      }
      case 'underline': {
        replacement = `<u>${selectedText}</u>`;
        if (!selectedText) {
          cursorPosition = selectionStart + 3; // after <u>
        }
        break;
      }
      case 'bullet': {
        // Convert selected lines to bullet list or insert new bullet
        if (selectedText) {
          const lines = selectedText.split('\n');
          replacement = lines.map(line => `• ${line}`).join('\n');
        } else {
          replacement = '• ';
        }
        cursorPosition = selectionStart + replacement.length;
        break;
      }
      case 'number': {
        // Convert selected lines to numbered list or insert new number
        if (selectedText) {
          const lines = selectedText.split('\n');
          replacement = lines.map((line, idx) => `${idx + 1}. ${line}`).join('\n');
        } else {
          replacement = '1. ';
        }
        cursorPosition = selectionStart + replacement.length;
        break;
      }
      default:
        break;
    }

    const newValue = value.slice(0, selectionStart) + replacement + value.slice(selectionEnd);
    const limitedValue = newValue.slice(0, DESCRIPTION_MAX_LENGTH);
    const cursorOffset = Math.min(cursorPosition, limitedValue.length);

    setAdForm(prev => ({
      ...prev,
      description: limitedValue
    }));

    requestAnimationFrame(() => {
      if (descriptionRef.current) {
        descriptionRef.current.focus();
        descriptionRef.current.setSelectionRange(cursorOffset, cursorOffset);
      }
    });
  };

  // Billing report state
  const formatDateInput = (date: Date) => date.toISOString().split('T')[0];
  const getPresetRange = (preset: BillingReportPreset) => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    
    switch (preset) {
      case 'last_7_days':
        start.setDate(end.getDate() - 7);
        break;
      case 'last_30_days':
        start.setDate(end.getDate() - 30);
        break;
      case 'this_month':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        break;
      default:
        break;
    }
    
    return { start, end };
  };

  const initialBillingRange = useMemo(() => getPresetRange('last_7_days'), []);
  const [billingPreset, setBillingPreset] = useState<BillingReportPreset>('last_7_days');
  const [billingStartDate, setBillingStartDate] = useState(formatDateInput(initialBillingRange.start));
  const [billingEndDate, setBillingEndDate] = useState(formatDateInput(initialBillingRange.end));
  const [billingResult, setBillingResult] = useState<BillingReportResult | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);

  const resetAdForm = () => {
    setAdForm({
      name: '',
      description: '',
      ...getDefaultDates(), // Reset to default dates
      banner_url: '',
      banner_file: null,
      status: 'active',
      priority: 'bronze',
      advertisement_mode: 'default',
      categories: [],
      region: '',
      button_text: '',
      badge_text: '',
      badge_color: 'red',
      promocode_delivery_methods: {
        web_display: true,
        email: false
      },
      target_url: '',
      target_url_enabled: false,
      // Provider information - reset to defaults
      provider_entity_id: '',
      provider_name: '',
      provider_logo_url: '',
      provider_privacy_policy_url: '',
      promocode_config: {
        type: 'none',
        static_code: '',
        pool_codes: [],
        pool_usage_limit: 1,
        used_codes: [],
        redirect_url: '',
        generation_rules: {
          length: 8,
          include_numbers: true,
          include_special_chars: false,
          prefix: '',
          suffix: ''
        },
        api_config: {
          endpoint: '',
          api_key: '',
          method: 'POST',
          headers: {},
          response_code_path: '',
          timeout_seconds: 15,
          fallback: {
            enabled: false,
            type: 'static',
            static_code: '',
            generation_rules: {
              length: 10,
              prefix: '',
              include_numbers: true,
              include_special_chars: false
            }
          }
        }
      }
    });
  };

  // Load click metrics when component mounts or user changes
  useEffect(() => {
    const loadClickMetrics = async () => {
      try {
        if (!user?.partner_id) return;
        
        await debugLogPartner(partnerDebugId, '📊 Loading real analytics from PocketBase...');
        
        // Get real analytics from PocketBase (current calendar month)
        const analytics = await pbClickTracker.getPartnerAnalytics(user.partner_id);
        
        await debugLogPartner(partnerDebugId, '✅ Analytics loaded:', analytics);
        
        setClickMetrics({
          totalClicks: analytics.total_clicks,
          totalViews: analytics.total_views,
          uniqueSessions: analytics.unique_sessions,
          conversionRate: analytics.conversion_rate,
          publicLinkClicks: analytics.public_link_clicks,
          promoRequests: analytics.promo_requests,
          totalDisplays: analytics.total_displays
        });
        
        // Fetch analytics for all partners to calculate tier (current calendar month)
        const allAnalytics = await Promise.all(
          partners.map(async (partner) => {
            try {
              const partnerAnalytics = await pbClickTracker.getPartnerAnalytics(partner.id);
              return {
                partnerId: partner.id,
                publicLinkClicks: partnerAnalytics.public_link_clicks
              };
            } catch (error) {
              console.warn('Failed to load analytics for partner:', partner.id, error);
              return {
                partnerId: partner.id,
                publicLinkClicks: 0
              };
            }
          })
        );
        
        setAllPartnersAnalytics(allAnalytics);
      } catch (error) {
        console.error('❌ Failed to load analytics:', error);
        // Don't crash the component - just use default values
        setClickMetrics({
          totalClicks: 0,
          totalViews: 0,
          uniqueSessions: 0,
          conversionRate: 0,
          publicLinkClicks: 0,
          promoRequests: 0,
          totalDisplays: 0
        });
        setAllPartnersAnalytics([]);
      }
    };

    // Use a timeout to avoid blocking initial render and add error handling
    try {
      const timeoutId = setTimeout(loadClickMetrics, 100);
      return () => clearTimeout(timeoutId);
    } catch (error) {
      console.error('Failed to set up analytics loading:', error);
    }
  }, [user?.partner_id, partners]);

  // Load billing report for current partner
  const loadBillingReport = async () => {
    if (!user?.partner_id) return;
    
    try {
      setBillingLoading(true);
      setBillingError(null);

      const start = new Date(billingStartDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(billingEndDate);
      end.setHours(23, 59, 59, 999);

      // Filter by current partner only
      const report = await getBillingReport({
        startDate: start,
        endDate: end,
        partnerId: user.partner_id
      });

      setBillingResult(report);
    } catch (error: any) {
      console.error('Billing report load failed:', error);
      setBillingError(error?.message || 'Failed to load billing report');
    } finally {
      setBillingLoading(false);
    }
  };

  // Load report when reporting tab is active
  useEffect(() => {
    if (activeTab === 'reporting' && user?.partner_id) {
      loadBillingReport();
    }
  }, [activeTab, billingStartDate, billingEndDate, user?.partner_id]);

  // Load entities when create/edit modal opens
  useEffect(() => {
    const loadEntities = async () => {
      if ((showCreateModal || editingAd) && user?.partner_id) {
        const { data } = await DataService.getEntities(user.partner_id);
        setPartnerEntities(data || []);
      }
    };
    loadEntities();
  }, [showCreateModal, editingAd, user?.partner_id]);

  // Handle entity selection - auto-fill provider fields from entity
  const handleEntitySelect = (entityId: string) => {
    if (entityId === '') {
      // Reset to partner defaults
      setAdForm(prev => ({
        ...prev,
        provider_entity_id: '',
        provider_name: '',
        provider_logo_url: '',
        provider_privacy_policy_url: ''
      }));
    } else if (entityId === 'custom') {
      // Switch to custom mode - keep current values but clear entity reference
      setAdForm(prev => ({
        ...prev,
        provider_entity_id: 'custom'
      }));
    } else {
      // Find entity and populate fields
      const entity = partnerEntities.find(e => e.id === entityId);
      if (entity) {
        setAdForm(prev => ({
          ...prev,
          provider_entity_id: entityId,
          provider_name: entity.name,
          provider_logo_url: entity.logo_url || '',
          provider_privacy_policy_url: entity.privacy_policy_url || ''
        }));
      }
    }
  };

  const handlePresetChange = (preset: BillingReportPreset) => {
    setBillingPreset(preset);
    if (preset === 'custom') return;
    const range = getPresetRange(preset);
    setBillingStartDate(formatDateInput(range.start));
    setBillingEndDate(formatDateInput(range.end));
  };

  const handleCreateAd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.partner_id) return;
    
    // Validate dates
    if (adForm.start_date && adForm.end_date) {
      const startDate = new Date(adForm.start_date);
      const endDate = new Date(adForm.end_date);
      
      if (endDate <= startDate) {
        setDateError('End date must be after start date');
        return;
      }
    }
    
    // Validate delivery methods for promocode types that generate codes
    if (adForm.promocode_config.type !== 'none' && adForm.promocode_config.type !== 'redirect') {
      if (!adForm.promocode_delivery_methods.web_display && !adForm.promocode_delivery_methods.email) {
        alert('Please select at least one delivery method for the promocode (Web Display or Email)');
        return;
      }
    }
    
    // Note: Quota validation removed - quotas only apply to published ads
    // Partners can create ads of any priority; quotas are enforced at publication time
    
    try {
      // Extract promocode config before creating ad
      const promocodeConfig = adForm.promocode_config;
      
      // Remove banner_file and promocode_config from the data being sent
      const { banner_file, promocode_config: _, ...adDataWithoutFile } = adForm;
      
      // Set has_promocode and promocode_type flags
      const dataToSend = {
        ...adDataWithoutFile,
        has_promocode: promocodeConfig?.type !== 'none',
        promocode_type: promocodeConfig?.type !== 'none' ? promocodeConfig.type : undefined,
        partner_id: user.partner_id,
        created_by: user.id,
        advertisement_mode: adForm.advertisement_mode || 'default',
        // Approval workflow fields - all new ads start as pending
        approval_status: 'pending' as const,
        approved_by: undefined,
        approved_at: undefined,
        rejection_reason: undefined
      };
      
      await debugLogPartner(partnerDebugId, '📤 Creating advertisement (without config):', dataToSend);
      await debugLogPartner(partnerDebugId, '📋 Promocode config to save:', promocodeConfig);
      
      // Create advertisement WITHOUT promocode_config (pass banner_file for upload)
      const createdAd = await createAdvertisement(dataToSend, adForm.banner_file);
      
      await debugLogPartner(partnerDebugId, '🔍 Created ad:', createdAd?.id);
      await debugLogPartner(partnerDebugId, '🔍 Promocode config type:', promocodeConfig?.type);
      await debugLogPartner(partnerDebugId, '🔍 Should save config?', createdAd && promocodeConfig && promocodeConfig.type !== 'none');
      
      // Save promocode config separately (if exists and not 'none')
      if (createdAd && promocodeConfig && promocodeConfig.type !== 'none') {
        await debugLogPartner(partnerDebugId, '📤 Saving promocode config for ad:', createdAd.id);
        
        // Clean up config based on type
        let cleanedConfig: any = { type: promocodeConfig.type };
        
        if (promocodeConfig.type === 'generic_static') {
          cleanedConfig.static_code = promocodeConfig.static_code;
        } else if (promocodeConfig.type === 'generic_pool') {
          cleanedConfig.pool_codes = promocodeConfig.pool_codes;
          cleanedConfig.pool_usage_limit = promocodeConfig.pool_usage_limit;
          cleanedConfig.used_codes = promocodeConfig.used_codes || [];
        } else if (promocodeConfig.type === 'personalized_generated') {
          cleanedConfig.generation_rules = {
            length: promocodeConfig.generation_rules.length,
            include_numbers: promocodeConfig.generation_rules.include_numbers,
            include_special_chars: promocodeConfig.generation_rules.include_special_chars,
            prefix: promocodeConfig.generation_rules.prefix || '',
            suffix: promocodeConfig.generation_rules.suffix || ''
          };
        } else if (promocodeConfig.type === 'redirect') {
          cleanedConfig.redirect_url = promocodeConfig.redirect_url;
        } else if (promocodeConfig.type === 'byop') {
          cleanedConfig.api_config = {
            endpoint: promocodeConfig.api_config.endpoint || '',
            api_key: promocodeConfig.api_config.api_key || '',
            method: promocodeConfig.api_config.method || 'POST',
            headers: promocodeConfig.api_config.headers || {},
            response_code_path: promocodeConfig.api_config.response_code_path || '',
            timeout_seconds: promocodeConfig.api_config.timeout_seconds || 15,
            fallback: promocodeConfig.api_config.fallback || {
              enabled: false,
              type: 'static',
              static_code: '',
              generation_rules: {
                length: 10,
                prefix: '',
                include_numbers: true,
                include_special_chars: false
              }
            }
          };
        }
        
        const { error: configError } = await DataService.savePromocodeConfig(
          createdAd.id,
          cleanedConfig
        );
        
        if (configError) {
          console.error('Failed to save promocode config:', configError);
          alert('Advertisement created but promocode config failed to save: ' + configError);
        }
      }
      
      setShowCreateModal(false);
      resetAdForm();
      setDateError('');
    } catch (error) {
      console.error('Failed to create advertisement:', error);
      alert(error instanceof Error ? error.message : 'Failed to create advertisement');
    }
  };

  const handleUpdateAd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingAd) {
      // Validate dates
      if (adForm.start_date && adForm.end_date) {
        const startDate = new Date(adForm.start_date);
        const endDate = new Date(adForm.end_date);
        
        if (endDate <= startDate) {
          setDateError('End date must be after start date');
          return;
        }
      }
      
      // Validate redirect URL when redirect type is selected
      if (adForm.promocode_config.type === 'redirect') {
        if (!adForm.promocode_config.redirect_url || adForm.promocode_config.redirect_url.trim() === '') {
          alert('Please enter a redirect URL when selecting "N/A - Redirect" option.');
          return;
        }
        // Basic URL validation
        try {
          new URL(adForm.promocode_config.redirect_url);
        } catch {
          alert('Please enter a valid URL (must start with http:// or https://).');
          return;
        }
      }
      
      // Note: Quota validation removed - quotas only apply to published ads
      // Partners can edit ads and change priorities; quotas are enforced at publication time
      
      try {
        // Extract promocode config before updating ad
        const promocodeConfig = adForm.promocode_config;
        
        // Remove banner_file and promocode_config from the data being sent
        const { banner_file, promocode_config: _, ...adDataWithoutFile } = adForm;
        
        // Set has_promocode and promocode_type flags
        const hasPromocode = promocodeConfig?.type !== 'none';
        const promocodeType = promocodeConfig?.type !== 'none' ? promocodeConfig.type : undefined;
        
        await debugLogPartner(partnerDebugId, '🔍 Setting promocode flags:', {
          promocodeConfigType: promocodeConfig?.type,
          hasPromocode,
          promocodeType
        });
        
        const updateData: any = {
          ...adDataWithoutFile,
          has_promocode: hasPromocode,
          promocode_type: promocodeType,
          advertisement_mode: adForm.advertisement_mode || 'default'
        };
        
        // If the ad was approved or rejected, reset to pending when edited
        if (editingAd.approval_status === 'approved' || editingAd.approval_status === 'rejected') {
          updateData.approval_status = 'pending';
          updateData.approved_by = undefined;
          updateData.approved_at = undefined;
          updateData.rejection_reason = undefined;
        }
        
        await debugLogPartner(partnerDebugId, '📤 Updating advertisement (without config):', updateData);
        
        // Update advertisement WITHOUT promocode_config (pass banner_file for upload)
        await updateAdvertisement(editingAd.id, updateData, adForm.banner_file);
        
        // Save promocode config separately (if exists)
        if (promocodeConfig && promocodeConfig.type !== 'none') {
          await debugLogPartner(partnerDebugId, '📤 Saving promocode config for ad:', editingAd.id);
          
          // Clean up config based on type
          let cleanedConfig: any = { type: promocodeConfig.type };
          
          if (promocodeConfig.type === 'generic_static') {
            cleanedConfig.static_code = promocodeConfig.static_code;
          } else if (promocodeConfig.type === 'generic_pool') {
            cleanedConfig.pool_codes = promocodeConfig.pool_codes;
            cleanedConfig.pool_usage_limit = promocodeConfig.pool_usage_limit;
            cleanedConfig.used_codes = promocodeConfig.used_codes || [];
          } else if (promocodeConfig.type === 'personalized_generated') {
            cleanedConfig.generation_rules = {
              length: promocodeConfig.generation_rules.length,
              include_numbers: promocodeConfig.generation_rules.include_numbers,
              include_special_chars: promocodeConfig.generation_rules.include_special_chars,
              prefix: promocodeConfig.generation_rules.prefix || '',
              suffix: promocodeConfig.generation_rules.suffix || ''
            };
          } else if (promocodeConfig.type === 'redirect') {
            cleanedConfig.redirect_url = promocodeConfig.redirect_url;
          } else if (promocodeConfig.type === 'byop') {
            cleanedConfig.api_config = {
              endpoint: promocodeConfig.api_config.endpoint || '',
              api_key: promocodeConfig.api_config.api_key || '',
              method: promocodeConfig.api_config.method || 'POST',
              headers: promocodeConfig.api_config.headers || {},
              response_code_path: promocodeConfig.api_config.response_code_path || '',
              timeout_seconds: promocodeConfig.api_config.timeout_seconds || 15,
              fallback: promocodeConfig.api_config.fallback || {
                enabled: false,
                type: 'static',
                static_code: '',
                generation_rules: {
                  length: 10,
                  prefix: '',
                  include_numbers: true,
                  include_special_chars: false
                }
              }
            };
          }
          
          const { error: configError } = await DataService.savePromocodeConfig(
            editingAd.id,
            cleanedConfig
          );
          
          if (configError) {
            console.error('Failed to save promocode config:', configError);
            alert('Advertisement updated but promocode config failed to save: ' + configError);
          }
        } else if (promocodeConfig?.type === 'none') {
          // If changed to 'none', delete the config
          await debugLogPartner(partnerDebugId, '🗑️ Deleting promocode config for ad:', editingAd.id);
          const { data: existingConfig } = await DataService.getPromocodeConfig(editingAd.id);
          if (existingConfig) {
            await DataService.deletePromocodeConfig(existingConfig.id);
          }
        }
        
        setShowCreateModal(false);
        setEditingAd(null);
        resetAdForm();
        setDateError('');
      } catch (error) {
        console.error('Failed to update advertisement:', error);
        alert(error instanceof Error ? error.message : 'Failed to update advertisement');
      }
    }
  };

  // File handling functions
  const handleFileSelect = (file: File) => {
    if (file && isValidImageFile(file)) {
      const url = URL.createObjectURL(file);
      setAdForm(prev => ({
        ...prev,
        banner_file: file,
        banner_url: url
      }));
    } else {
      alert('Please select a valid image file (JPG, PNG, GIF, WebP)');
    }
  };

  const isValidImageFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    return validTypes.includes(file.type) && file.size <= maxSize;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // CSV upload handler for promocode pool
  const handleCSVUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const rawCodes = text
        .split(/[\r\n,]+/)
        .map(code => code.trim())
        .filter(code => code.length > 0);
      
      // Sanitize codes to ASCII only and filter out any that become empty
      const codes = rawCodes
        .map(code => sanitizeToAscii(code.toUpperCase()))
        .filter(code => code.length > 0);
      
      // Warn if some codes were filtered out
      if (codes.length < rawCodes.length) {
        alert(`Warning: ${rawCodes.length - codes.length} code(s) contained non-ASCII characters and were sanitized or removed.`);
      }
      
      setAdForm(prev => ({
        ...prev,
        promocode_config: {
          ...prev.promocode_config,
          pool_codes: codes
        }
      }));
    };
    reader.readAsText(file);
  };

  const startEditAd = async (ad: any) => {
    setEditingAd(ad);
    
    // Convert dates to YYYY-MM-DD format for date inputs
    const formatDateForInput = (dateString: string) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      } catch {
        return dateString;
      }
    };
    
    // Default empty config
    const defaultConfig = {
      type: 'none' as any,
      static_code: '',
      pool_codes: [],
      pool_usage_limit: 1,
      used_codes: [],
      redirect_url: '',
      generation_rules: {
        length: 8,
        include_numbers: true,
        include_special_chars: false,
        prefix: '',
        suffix: ''
      },
      api_config: {
        endpoint: '',
        api_key: '',
        method: 'POST' as 'GET' | 'POST',
        headers: {},
        response_code_path: '',
        timeout_seconds: 15,
        fallback: {
          enabled: false,
          type: 'static' as 'static' | 'dynamic',
          static_code: '',
          generation_rules: {
            length: 10,
            prefix: '',
            include_numbers: true,
            include_special_chars: false
          }
        }
      }
    };
    
    // Load secure promocode config for editing
    let promocodeConfig: typeof defaultConfig = defaultConfig;
    // Always try to load config for backward compatibility with existing ads
    try {
      await debugLogPartner(partnerDebugId, '📥 Loading promocode config for ad:', ad.id);
      const { data: config, error } = await DataService.getPromocodeConfig(ad.id);
      
      if (error) {
        await debugLogPartner(partnerDebugId, 'ℹ️ No promocode config found:', error);
      } else if (config) {
        // Merge loaded config with defaults to ensure all properties exist
        promocodeConfig = {
          ...defaultConfig,
          ...config,
          generation_rules: {
            ...defaultConfig.generation_rules,
            ...(config.generation_rules || {})
          },
          api_config: {
            ...defaultConfig.api_config,
            ...(config.api_config || {}),
            fallback: {
              ...defaultConfig.api_config.fallback,
              ...(config.api_config?.fallback || {}),
              generation_rules: {
                ...defaultConfig.api_config.fallback.generation_rules,
                ...(config.api_config?.fallback?.generation_rules || {})
              }
            }
          }
        };
        await debugLogPartner(partnerDebugId, '✅ Loaded promocode config:', promocodeConfig);
      }
    } catch (error) {
      console.error('Failed to load promocode config:', error);
    } finally {
      setAdForm({
        name: ad.name,
        description: ad.description,
        start_date: formatDateForInput(ad.start_date),
        end_date: formatDateForInput(ad.end_date),
        banner_url: ad.banner_url,
        banner_file: null, // Reset file when editing existing ad
        status: ad.status,
        priority: ad.priority,
        advertisement_mode: ad.advertisement_mode || 'default',
        categories: ad.categories || [],
        region: ad.region || '',
        button_text: ad.button_text || '',
        badge_text: ad.badge_text || '',
        badge_color: ad.badge_color || 'red',
        promocode_delivery_methods: ad.promocode_delivery_methods || {
          web_display: true,
          email: false
        },
        target_url: ad.target_url || '',
        target_url_enabled: ad.target_url_enabled || false,
        provider_entity_id: ad.provider_entity_id || '',
        provider_name: ad.provider_name || '',
        provider_logo_url: ad.provider_logo_url || '',
        provider_privacy_policy_url: ad.provider_privacy_policy_url || '',
        promocode_config: promocodeConfig
      });
      setShowCreateModal(true);
    }
  };

  const partnerAds = advertisements.filter(ad => ad.partner_id === user?.partner_id);
  const partnerCampaignsFiltered = partnerCampaigns.filter(pc => pc.partner_id === user?.partner_id);

  // Load promocode configs for all advertisements
  useEffect(() => {
    const loadPromocodeConfigs = async () => {
      if (!partnerAds || partnerAds.length === 0) return;
      
      const configs: Record<string, any> = {};
      
      await Promise.all(
        partnerAds.map(async (ad) => {
          try {
            const { data } = await DataService.getPromocodeConfig(ad.id);
            if (data) {
              configs[ad.id] = data;
            }
          } catch (error) {
            console.warn('Failed to load config for ad:', ad.id, error);
          }
        })
      );
      
      setPromocodeConfigs(configs);
    };
    
    loadPromocodeConfigs();
  }, [partnerAds.length]); // Use length to avoid infinite loop

  useEffect(() => {
    (async () => {
      await debugLogPartner(partnerDebugId, '🔍 Advertisement Debug Snapshot:', {
        partnerId: user.partner_id,
        totalAds: advertisements.length,
        partnerAds: partnerAds.length,
        partnerAdsDetails: partnerAds.map(ad => ({
          id: ad.id,
          name: ad.name,
          status: ad.status,
          priority: ad.priority,
          partner_id: ad.partner_id
        }))
      });
    })();
  }, [partnerDebugId, user.partner_id, advertisements.length, partnerAds.length]);

  // Filter promo requests by matching advertisement_id to partner's advertisements
  const campaignPromoRequests = promoCodeRequests.filter(req => 
    partnerAds.some(ad => ad.id === req.advertisement_id)
  );

  useEffect(() => {
    (async () => {
      await debugLogPartner(partnerDebugId, '📊 Promo Requests Debug:', {
        totalPromoRequests: promoCodeRequests.length,
        partnerAds: partnerAds.length,
        partnerCampaigns: partnerCampaignsFiltered.length,
        campaignPromoRequests: campaignPromoRequests.length,
        promoRequestsSample: promoCodeRequests.slice(0, 3),
        filteredRequests: campaignPromoRequests
      });
    })();
  }, [partnerDebugId, promoCodeRequests.length, partnerAds.length, partnerCampaignsFiltered.length, campaignPromoRequests.length]);

  // Get current partner's regions
  const currentPartner = partners.find(p => p.id === user?.partner_id);
  const partnerRegions = currentPartner?.region || [];

  useEffect(() => {
    (async () => {
      await debugLogPartner(partnerDebugId, '🔍 Partner Info Snapshot:', {
        partnerId: user.partner_id,
        currentPartner: currentPartner ? {
          id: currentPartner.id,
          name: currentPartner.name,
          regions: currentPartner.region,
          status: currentPartner.status
        } : null,
        partnerRegions
      });
    })();
  }, [partnerDebugId, user.partner_id, currentPartner?.id, partnerRegions.length]);

  const publishedAdIds = useMemo(() => {
    if (!user?.partner_id) return new Set<string>();

    const partnerCampaignsForPartner = partnerCampaigns.filter(
      campaign => campaign.partner_id === user.partner_id
    );

    const activePartnerCampaignIds = new Set(
      partnerCampaignsForPartner
        .filter(campaign => campaign.status === 'active')
        .map(campaign => campaign.id)
    );

    if (activePartnerCampaignIds.size === 0) {
      return new Set<string>();
    }

    const publishedPartnerCampaignIds = new Set<string>();

    marketingCampaigns.forEach((marketingCampaign) => {
      if (marketingCampaign.status !== 'active') return;

      marketingCampaign.partner_campaign_ids.forEach((partnerCampaignId) => {
        if (activePartnerCampaignIds.has(partnerCampaignId)) {
          publishedPartnerCampaignIds.add(partnerCampaignId);
        }
      });
    });

    if (publishedPartnerCampaignIds.size === 0) {
      return new Set<string>();
    }

    const adIds = new Set<string>();
    partnerCampaignsForPartner.forEach((campaign) => {
      if (!publishedPartnerCampaignIds.has(campaign.id)) return;
      campaign.advertisement_ids?.forEach((adId) => adIds.add(adId));
    });

    return adIds;
  }, [user?.partner_id, partnerCampaigns, marketingCampaigns]);

  const isAdPublished = (adId: string) => {
    const ad = partnerAds.find(a => a.id === adId);
    if (!ad) return false;
    
    // Independent mode: published when approved (not in campaigns)
    if (ad.advertisement_mode === 'independent') {
      return ad.approval_status === 'approved';
    }
    
    // Universal mode: published when approved (available both in campaigns and via direct link)
    if (ad.advertisement_mode === 'universal') {
      return ad.approval_status === 'approved';
    }
    
    // Default mode: published when in active marketing campaigns
    return publishedAdIds.has(adId);
  };

  const publishedAdsCount = partnerAds.filter(ad => isAdPublished(ad.id)).length;

  const filteredAds = partnerAds.filter(ad => {
    const matchesSearch = ad.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ad.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ad.status === statusFilter;
    const matchesRegion = regionFilter === 'all' || ad.region === regionFilter;
    
    void debugLogPartner(partnerDebugId, `🔍 Filtering ad "${ad.name}" (id: ${ad.id}):`, {
      matchesSearch,
      matchesStatus,
      matchesRegion,
      partnerId: ad.partner_id,
      adRegion: ad.region
    });
    
    const shouldInclude = matchesSearch && matchesStatus && matchesRegion;
    void debugLogPartner(partnerDebugId, `   → Should include: ${shouldInclude}`);
    
    return shouldInclude;
  }).sort((a, b) => {
    // Sort by priority: platinum > gold > silver > bronze
    const priorityOrder = { platinum: 1, gold: 2, silver: 3, bronze: 4 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPublicationStatusColor = (adId: string) => {
    return isAdPublished(adId)
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : 'bg-gray-50 text-gray-600 border border-gray-200';
  };

  const getPriorityColor = (priority: AdvertisementPriority) => {
    switch (priority) {
      case 'platinum': return 'bg-purple-100 text-purple-800';
      case 'gold': return 'bg-yellow-100 text-yellow-800';
      case 'silver': return 'bg-gray-200 text-gray-800';
      case 'bronze': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityLabel = (priority: AdvertisementPriority) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const getApprovalStatusColor = (status?: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-yellow-100 text-yellow-800 border-yellow-300'; // Treat undefined as pending
    }
  };

  const getApprovalStatusLabel = (status?: string) => {
    switch (status) {
      case 'approved': return '✓ Approved';
      case 'rejected': return '✗ Rejected';
      case 'pending': return '⏳ Pending Review';
      default: return '⏳ Pending Review'; // Treat undefined as pending
    }
  };

  // Get count of ads by priority for current partner
  // Only count APPROVED ads (regardless of active/inactive status)
  // Draft and pending ads don't count against quota
  const getAdCountByPriority = (priority: AdvertisementPriority) => {
    return partnerAds.filter(ad => 
      ad.priority === priority && 
      ad.approval_status === 'approved' &&
      isAdPublished(ad.id)
    ).length;
  };

  // Check if partner can create more ads of a specific priority
  const canCreateAdWithPriority = (priority: AdvertisementPriority) => {
    if (!currentPartner?.priority_quotas) return true; // No quotas set
    const currentCount = getAdCountByPriority(priority);
    const quota = currentPartner.priority_quotas[priority];
    return currentCount < quota;
  };

  // Get remaining quota for a priority
  const getRemainingQuota = (priority: AdvertisementPriority) => {
    if (!currentPartner?.priority_quotas) return Infinity;
    const currentCount = getAdCountByPriority(priority);
    const quota = currentPartner.priority_quotas[priority];
    return Math.max(0, quota - currentCount);
  };

  // Check if partner has access to a priority
  const hasPriorityAccess = (priority: AdvertisementPriority): boolean => {
    if (!currentPartner?.priority_quotas) return true;
    return (currentPartner.priority_quotas[priority] || 0) > 0;
  };

  // Get the first available priority for the partner (with remaining quota)
  const getFirstAvailablePriority = (): AdvertisementPriority => {
    if (!currentPartner?.priority_quotas) return 'bronze';
    
    const priorities: AdvertisementPriority[] = ['platinum', 'gold', 'silver', 'bronze'];
    for (const priority of priorities) {
      if (hasPriorityAccess(priority) && getRemainingQuota(priority) > 0) {
        return priority;
      }
    }
    // Fallback to first available priority even if quota is full
    for (const priority of priorities) {
      if (hasPriorityAccess(priority)) {
        return priority;
      }
    }
    return 'bronze'; // Final fallback
  };

  // Open create modal with correct default priority
  const openCreateModal = () => {
    resetAdForm();
    setAdForm(prev => ({
      ...prev,
      priority: getFirstAvailablePriority()
    }));
    setShowCreateModal(true);
  };

  const availableCategories = useMemo<AdvertisementCategory[]>(() => {
    const categorySet = new Set<AdvertisementCategory>();

    filters
      .filter(filter => filter.status === 'active')
      .forEach(filter => categorySet.add(filter.name as AdvertisementCategory));

    // Ensure currently selected categories remain selectable even if not returned by API
    adForm.categories.forEach(category => categorySet.add(category));

    if (categorySet.size === 0) {
      advertisements.forEach(ad => {
        (ad.categories || []).forEach(category => categorySet.add(category as AdvertisementCategory));
      });
    }

    return Array.from(categorySet).sort((a, b) => a.localeCompare(b));
  }, [filters, adForm.categories, advertisements]);

  const toggleCategory = (category: AdvertisementCategory) => {
    void debugLogPartner(partnerDebugId, 'Toggling category:', category);
    void debugLogPartner(partnerDebugId, 'Current categories:', adForm.categories);
    
    setAdForm(prev => {
      const newCategories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : prev.categories.length < 3
          ? [...prev.categories, category]
          : prev.categories;
      
      void debugLogPartner(partnerDebugId, 'New categories:', newCategories);
      
      return {
        ...prev,
        categories: newCategories
      };
    });
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    valueColor?: string;
  }> = ({ title, value, icon, color, valueColor }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold mt-2 ${valueColor || 'text-gray-900'}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const isExpired = (endDate: string) => {
    return new Date(endDate) < new Date();
  };

  const getPartner = () => {
    return partners.find(p => p.id === user?.partner_id);
  };

  const getPartnerName = () => {
    const partner = getPartner();
    return partner?.name || 'Partner';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              {(() => {
                const partner = getPartner();
                const logoUrl = partner ? getPartnerLogoUrl(partner) : '';
                return logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt={`${getPartnerName()} logo`}
                    className="w-24 h-24 rounded-lg mr-4 object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null;
              })()}
              <div className={`bg-blue-100 p-6 rounded-lg mr-4 ${(() => {
                const partner = getPartner();
                return partner && getPartnerLogoUrl(partner) ? 'hidden' : '';
              })()}`}>
                <Image className="text-blue-600" size={48} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {getPartnerName()}
                </h1>
                <p className="text-gray-600 mt-2">
                  Create and manage your advertising campaigns
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center relative">
              {(() => {
                const activeCampaign = partnerCampaignsFiltered.find(pc => pc.status === 'active');
                const hasMarketingCampaign = activeCampaign && marketingCampaigns.some(mc => 
                  mc.status === 'active' && 
                  mc.partner_campaign_ids.includes(activeCampaign.id) &&
                  mc.region === activeCampaign.region
                );
                const isDisabled = !hasMarketingCampaign;

                return (
                  <button
                    onClick={() => {
                      if (isDisabled) return;
                      
                      const activeCampaign = partnerCampaignsFiltered.find(pc => pc.status === 'active');
                      if (activeCampaign) {
                        const marketingCampaign = marketingCampaigns.find(mc => 
                          mc.status === 'active' && 
                          mc.partner_campaign_ids.includes(activeCampaign.id) &&
                          mc.region === activeCampaign.region
                        );
                        
                        if (marketingCampaign) {
                          setShowMagicLinkDropdown(!showMagicLinkDropdown);
                        }
                      }
                    }}
                    disabled={isDisabled}
                    className={`flex items-center justify-center px-4 py-3 rounded-lg transition duration-200 text-sm font-semibold whitespace-nowrap ${
                      isDisabled 
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-60' 
                        : 'bg-gradient-to-r from-green-500 to-blue-600 text-white hover:from-green-600 hover:to-blue-700 cursor-pointer'
                    }`}
                    title={isDisabled ? 'Your campaign must be included in a global marketing campaign first' : 'Get your magic link'}
                  >
                    <span className="mr-2">✨</span>
                    <span>Get my magic link</span>
                    <span className="ml-2">✨</span>
                  </button>
                );
              })()}

              {/* Magic Link Dropdown */}
              {showMagicLinkDropdown && (() => {
                const activeCampaign = partnerCampaignsFiltered.find(pc => pc.status === 'active');
                const marketingCampaign = activeCampaign ? marketingCampaigns.find(mc => 
                  mc.status === 'active' && 
                  mc.partner_campaign_ids.includes(activeCampaign.id) &&
                  mc.region === activeCampaign.region
                ) : null;
                
                if (!marketingCampaign) return null;
                
                // Get locale from campaign region for instant language detection
                const campaignLocale = marketingCampaign.locale || 
                  (marketingCampaign.region ? getLocaleFromRegion(marketingCampaign.region) : 'en');
                const magicLink = `${window.location.origin}/campaign/${marketingCampaign.id}?domainuuid=${user?.partner_id}&locale=${campaignLocale}`;
                
                const buttonDesigns = [
                  {
                    name: 'Green to Blue',
                    preview: 'bg-gradient-to-r from-green-500 to-blue-600 text-white',
                    code: `<a href="${magicLink}" class="inline-block px-6 py-3 bg-gradient-to-r from-green-500 to-blue-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-blue-700 transition duration-200">\n  Receive a gift\n</a>`
                  },
                  {
                    name: 'Purple Gradient',
                    preview: 'bg-gradient-to-r from-purple-500 to-pink-600 text-white',
                    code: `<a href="${magicLink}" class="inline-block px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-pink-700 transition duration-200">\n  Receive a gift\n</a>`
                  },
                  {
                    name: 'Orange Gradient',
                    preview: 'bg-gradient-to-r from-orange-500 to-red-600 text-white',
                    code: `<a href="${magicLink}" class="inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-700 transition duration-200">\n  Receive a gift\n</a>`
                  }
                ];

                return (
                  <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[600px] overflow-y-auto">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-900 mb-2">Magic Link Options</h3>
                      <p className="text-xs text-gray-500">Share your magic link or embed a button on your website</p>
                    </div>

                    {/* Copy Link Option */}
                    <div className="p-4 border-b border-gray-200">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(magicLink);
                          alert('✨ Magic link copied to clipboard!');
                        }}
                        className="w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition duration-200"
                      >
                        <div className="flex items-center">
                          <Copy className="text-blue-600 mr-3" size={20} />
                          <div className="text-left">
                            <p className="font-medium text-gray-900">Copy Link</p>
                            <p className="text-xs text-gray-500">Copy URL to clipboard</p>
                          </div>
                        </div>
                        <span className="text-blue-600 text-xs">Copy</span>
                      </button>
                    </div>

                    {/* Button Designs */}
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Embed Button Designs</h4>
                      <p className="text-xs text-gray-500 mb-3">Click any button to copy its embed code</p>
                      <div className="space-y-3">
                        {buttonDesigns.map((design, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              navigator.clipboard.writeText(design.code);
                              setCopiedSnippet(design.name);
                              setTimeout(() => setCopiedSnippet(null), 2000);
                            }}
                            className="w-full border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50 transition duration-200 group"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm font-medium text-gray-900">{design.name}</p>
                              {copiedSnippet === design.name ? (
                                <span className="flex items-center text-green-600 text-xs font-medium">
                                  <Check size={14} className="mr-1" /> Copied!
                                </span>
                              ) : (
                                <span className="flex items-center text-gray-400 group-hover:text-blue-600 text-xs">
                                  <Copy size={14} className="mr-1" /> Click to copy
                                </span>
                              )}
                            </div>
                            
                            {/* Preview */}
                            <div className="p-4 bg-gray-50 rounded-lg flex justify-center">
                              <div className={`px-6 py-3 rounded-lg font-semibold text-sm ${design.preview}`}>
                                Receive a gift
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Close Button */}
                    <div className="p-4 border-t border-gray-200">
                      <button
                        onClick={() => setShowMagicLinkDropdown(false)}
                        className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition duration-200"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 overflow-x-auto px-4 sm:px-0">
              {[
                { id: 'overview', label: 'Overview', icon: Activity },
                { id: 'advertisements', label: 'Advertisements', icon: Image },
                { id: 'campaigns', label: 'Campaigns', icon: Target },
                { id: 'promo-requests', label: 'Promo Requests', icon: Mail },
                { id: 'reporting', label: 'Reporting', icon: TrendingUp },
                { id: 'integrations', label: 'Integrations', icon: Settings },
                { id: 'entities', label: 'Entities', icon: Building2 },
                { id: 'logs', label: 'Activity Logs', icon: Monitor }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as any)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={20} className="mr-2" />
                  {label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Period indicator */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-gray-500" />
                <span className="text-sm text-gray-600">
                  Showing metrics for <span className="font-semibold text-gray-900">{new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                </span>
              </div>
            </div>
            
            {/* Metrics - 3 rows of 4 items */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Row 1 */}
              <MetricCard
                title="Total Advertisements"
                value={partnerAds.length}
                icon={<Files className="text-white" size={24} />}
                color="bg-orange-500"
              />
              <MetricCard
                title="Published Advertisements"
                value={publishedAdsCount}
                icon={<FileCheck2 className="text-white" size={24} />}
                color="bg-green-500"
              />
              <MetricCard
                title="Campaigns"
                value={partnerCampaignsFiltered.length}
                icon={<GalleryVerticalEnd className="text-white" size={24} />}
                color="bg-purple-500"
              />
              <MetricCard
                title="Approved Campaigns"
                value={partnerCampaignsFiltered.filter(pc => 
                  marketingCampaigns.some(mc => 
                    mc.status === 'active' && 
                    mc.partner_campaign_ids.includes(pc.id) &&
                    mc.region === pc.region
                  )
                ).length}
                icon={<CheckCircle className="text-white" size={24} />}
                color="bg-emerald-500"
              />
              
              {/* Row 2 */}
              <MetricCard
                title="Magic Link Clicks"
                value={clickMetrics.publicLinkClicks}
                icon={<Target className="text-white" size={24} />}
                color="bg-indigo-500"
              />
              <MetricCard
                title="Unique Magic Link Visitors"
                value={clickMetrics.uniqueSessions}
                icon={<Users className="text-white" size={24} />}
                color="bg-green-500"
              />
              <MetricCard
                title="Total Advertisement Displays"
                value={clickMetrics.totalDisplays}
                icon={<Monitor className="text-white" size={24} />}
                color="bg-cyan-500"
              />
              <MetricCard
                title="Click-Through Rate"
                value={clickMetrics.totalDisplays > 0 ? `${Math.round((clickMetrics.totalViews / clickMetrics.totalDisplays) * 100)}%` : '0%'}
                icon={<MousePointer className="text-white" size={24} />}
                color="bg-yellow-500"
              />
              
              {/* Row 3 */}
              <MetricCard
                title="Total Advertisement Views"
                value={clickMetrics.totalViews}
                icon={<Eye className="text-white" size={24} />}
                color="bg-purple-500"
              />
              <MetricCard
                title="Promo Requests"
                value={campaignPromoRequests.length}
                icon={<Mail className="text-white" size={24} />}
                color="bg-orange-500"
              />
              <MetricCard
                title="Conversion Rate"
                value={clickMetrics.totalViews > 0 ? `${Math.round((campaignPromoRequests.length / clickMetrics.totalViews) * 100)}%` : '0%'}
                icon={<Activity className="text-white" size={24} />}
                color="bg-teal-500"
              />
              <MetricCard
                title="Current Engagement Tier"
                value={(() => {
                  if (allPartnersAnalytics.length === 0) return 'D';
                  
                  // Sort by magic link clicks descending
                  const sortedPartners = [...allPartnersAnalytics].sort((a, b) => b.publicLinkClicks - a.publicLinkClicks);
                  
                  // Find current partner's rank
                  const currentPartnerIndex = sortedPartners.findIndex(p => p.partnerId === user?.partner_id);
                  const totalPartners = sortedPartners.length;
                  
                  if (currentPartnerIndex === -1 || totalPartners === 0) return 'D';
                  
                  // Calculate percentile (0 = top, 1 = bottom)
                  const percentile = currentPartnerIndex / totalPartners;
                  
                  // Assign tier based on percentile
                  if (percentile < 0.10) return 'A'; // Top 10%
                  if (percentile < 0.30) return 'B'; // Next 20% (10-30%)
                  if (percentile < 0.60) return 'C'; // Next 30% (30-60%)
                  return 'D'; // Bottom 40% (60-100%)
                })()}
                icon={<TrendingUp className="text-white" size={24} />}
                color="bg-red-500"
                valueColor="text-violet-600"
              />
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Advertisements</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {partnerAds.slice(0, 3).map((ad) => (
                      <div key={ad.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <img
                            src={ad.banner_url}
                            alt={ad.name}
                            className="w-12 h-8 object-cover rounded"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=800';
                            }}
                          />
                          <div>
                            <p className="font-medium text-gray-900">{ad.name}</p>
                            <p className="text-sm text-gray-500">
                              Valid until {new Date(ad.end_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                          isExpired(ad.end_date) ? 'expired' : ad.status
                        )}`}>
                          {isExpired(ad.end_date) ? 'Expired' : ad.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Promo Requests</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {campaignPromoRequests.length > 0 ? (
                      campaignPromoRequests.slice(0, 3).map((request: any) => (
                        <div key={request.id} className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg items-center">
                          <div>
                            <p className="font-medium text-gray-900">{request.email}</p>
                            {request.advertisement_name && (
                              <p className="text-sm font-medium text-blue-600">
                                Selected: {request.advertisement_name}
                              </p>
                            )}
                          </div>
                          <div className="text-center">
                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(request.status)}`}>
                              {request.status}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {new Date(request.requested_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-gray-500 text-sm">No promo code requests yet</p>
                        <p className="text-gray-400 text-xs mt-1">
                          Requests will appear here when users request promo codes from your advertisements
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Advertisements Tab */}
        {activeTab === 'advertisements' && (
          <div className="space-y-6">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search advertisements..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
                
                {/* Region Filter */}
                {partnerRegions.length > 1 && (
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <select
                      value={regionFilter}
                      onChange={(e) => setRegionFilter(e.target.value)}
                      className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                    >
                      <option value="all">All Regions</option>
                      {partnerRegions.map((regionCode) => (
                        <option key={regionCode} value={regionCode}>
                          {getCountryNames([regionCode])[0]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                {/* Quotas Display - Only show if partner has at least one allocated quota */}
                {currentPartner?.priority_quotas && (
                  hasPriorityAccess('platinum') || hasPriorityAccess('gold') || 
                  hasPriorityAccess('silver') || hasPriorityAccess('bronze')
                ) && (
                  <div className="flex items-center gap-3 text-xs px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-gray-700 font-medium">Your quotas:</span>
                    <div className="flex items-center gap-3">
                    {hasPriorityAccess('platinum') && currentPartner.priority_quotas.platinum > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-600">🟣 Platinum:</span>
                        <span className={`font-medium ${getRemainingQuota('platinum') === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {getAdCountByPriority('platinum')}/{currentPartner.priority_quotas.platinum}
                        </span>
                      </div>
                    )}
                    {hasPriorityAccess('gold') && currentPartner.priority_quotas.gold > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-600">🟡 Gold:</span>
                        <span className={`font-medium ${getRemainingQuota('gold') === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {getAdCountByPriority('gold')}/{currentPartner.priority_quotas.gold}
                        </span>
                      </div>
                    )}
                    {hasPriorityAccess('silver') && currentPartner.priority_quotas.silver > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-600">⚪ Silver:</span>
                        <span className={`font-medium ${getRemainingQuota('silver') === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {getAdCountByPriority('silver')}/{currentPartner.priority_quotas.silver}
                        </span>
                      </div>
                    )}
                    {hasPriorityAccess('bronze') && currentPartner.priority_quotas.bronze > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-600">🟠 Bronze:</span>
                        <span className={`font-medium ${getRemainingQuota('bronze') === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {getAdCountByPriority('bronze')}/{currentPartner.priority_quotas.bronze}
                        </span>
                      </div>
                    )}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={openCreateModal}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center"
                >
                  <Plus size={20} className="mr-2" />
                  New Advertisement
                </button>
              </div>
            </div>
            {/* Advertisements Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAds.map((ad) => {
                const promoTypeKey = (ad.promocode_type as PromocodeType) || 'default';
                const promoTypeStyle = PROMOCODE_TYPE_STYLES[promoTypeKey] || PROMOCODE_TYPE_STYLES.default;

                return (
                <div key={ad.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition duration-200 flex flex-col">
                  <div className="aspect-video bg-gray-100 relative overflow-hidden">
                    <img
                      src={ad.banner_url}
                      alt={ad.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=800';
                      }}
                    />
                    <div className="absolute top-4 right-4 flex gap-2 flex-wrap justify-end">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(ad.priority)}`}>
                        {getPriorityLabel(ad.priority)}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        isExpired(ad.end_date) ? 'expired' : ad.status
                      )}`}>
                        {isExpired(ad.end_date) ? 'Expired' : ad.status}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPublicationStatusColor(ad.id)}`}>
                        {isAdPublished(ad.id) ? 'Published' : 'Not Published'}
                      </span>
                    </div>
                    {/* Approval Status Badge - Bottom Left */}
                    <div className="absolute bottom-4 left-4">
                      <span className={`px-3 py-1.5 text-xs font-semibold rounded-lg border-2 ${getApprovalStatusColor(ad.approval_status)} shadow-sm`}>
                        {getApprovalStatusLabel(ad.approval_status)}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{ad.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{ad.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <Calendar size={16} className="mr-2" />
                        <span>{new Date(ad.start_date).toLocaleDateString('en-GB')} - {new Date(ad.end_date).toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>
                    
                    {/* Region Display */}
                    {ad.region && (
                      <div className="flex items-center text-sm text-gray-600 mb-3">
                        <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                          🌍 {getCountryNames([ad.region])[0]} ({ad.region})
                        </span>
                      </div>
                    )}
                    {ad.categories && ad.categories.length > 0 && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-1">
                          {ad.categories.map((category, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                            >
                              {category}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Promo Code Configuration Display */}
                    <div className="mb-4">
                      {/* N/A - No Promocode */}
                      {!ad.has_promocode && (
                        <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-700">📋 Direct Contact</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            Customers will be contacted directly via their submitted email address. No promocode will be generated.
                          </div>
                        </div>
                      )}
                      
                      {ad.has_promocode && ad.promocode_type && (
                        <div className={`${promoTypeStyle.container} rounded-lg p-3`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-medium ${promoTypeStyle.label}`}>🎫 Promocode Type</span>
                          </div>
                          <div className="text-xs text-gray-700 font-medium">
                            {ad.promocode_type === 'generic_static' && '🏷️ Static Code'}
                            {ad.promocode_type === 'generic_pool' && '🎫 Code Pool'}
                            {ad.promocode_type === 'personalized_generated' && '✨ Personalized'}
                            {ad.promocode_type === 'byop' && '🔌 API Integration'}
                            {ad.promocode_type === 'redirect' && '🔗 Redirect'}
                          </div>
                          
                          {/* Pool Usage Progress Bar */}
                          {ad.promocode_type === 'generic_pool' && promocodeConfigs[ad.id] && (() => {
                            const config = promocodeConfigs[ad.id];
                            const totalCodes = config.pool_codes?.length || 0;
                            const usedCodes = config.used_codes?.length || 0;
                            const remainingCodes = totalCodes - usedCodes;
                            const usagePercent = totalCodes > 0 ? (usedCodes / totalCodes) * 100 : 0;
                            
                            return (
                              <div className="mt-3 space-y-2">
                                <div className="flex justify-between items-center text-xs">
                                  <span className={promoTypeStyle.label}>Pool Usage</span>
                                  <span className={`font-semibold ${promoTypeStyle.label}`}>
                                    {remainingCodes} / {totalCodes} codes left
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                  <div 
                                    className={`h-full transition-all duration-300 ${
                                      usagePercent >= 90 ? 'bg-red-500' : 
                                      usagePercent >= 70 ? 'bg-yellow-500' : 
                                      'bg-teal-500'
                                    }`}
                                    style={{ width: `${usagePercent}%` }}
                                  />
                                </div>
                                {remainingCodes === 0 && (
                                  <div className="text-xs text-red-600 font-semibold">
                                    ⚠️ Pool exhausted - ad will be auto-suspended
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          
                          <div className={`text-xs mt-1 ${promoTypeStyle.note}`}>
                            Click "Edit" to view full configuration
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Rejection Reason Display */}
                    {ad.approval_status === 'rejected' && ad.rejection_reason && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                          <span className="text-red-600 mr-2 mt-0.5">⚠️</span>
                          <div>
                            <p className="text-xs font-semibold text-red-800 mb-1">Rejection Reason:</p>
                            <p className="text-xs text-red-700">{ad.rejection_reason}</p>
                            {ad.approved_at && (
                              <p className="text-xs text-red-600 mt-1">
                                Rejected on {new Date(ad.approved_at).toLocaleString('en-GB')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex space-x-2 mt-auto">
                      <button
                        onClick={() => startEditAd(ad)}
                        className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 transition duration-200 flex items-center justify-center"
                      >
                        <Edit size={16} className="mr-2" />
                        Edit
                      </button>
                      {(ad.advertisement_mode === 'independent' || ad.advertisement_mode === 'universal') && ad.approval_status === 'approved' && (
                        <button
                          onClick={() => {
                            const directLink = `${window.location.origin}/ad/${ad.id}`;
                            navigator.clipboard.writeText(directLink);
                            alert(`Direct link copied to clipboard!\n\n${directLink}`);
                          }}
                          className="flex-1 bg-green-50 text-green-600 px-3 py-2 rounded-lg hover:bg-green-100 transition duration-200 flex items-center justify-center"
                          title="Copy direct link"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Link
                        </button>
                      )}
                      <button
                        onClick={() => deleteAdvertisement(ad.id)}
                        className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 transition duration-200 flex items-center justify-center"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );})}
            </div>

            {filteredAds.length === 0 && (
              <div className="text-center py-12">
                <Image size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No advertisements found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filter criteria'
                    : 'Create your first advertisement to get started'
                  }
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <button
                    onClick={openCreateModal}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200"
                  >
                    Create Advertisement
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <PartnerCampaignManager />
        )}

        {/* Promo Requests Tab */}
        {activeTab === 'promo-requests' && (
          <PromoRequestsManager />
        )}

        {/* Reporting Tab */}
        {activeTab === 'reporting' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col gap-6">
                {/* Date Range Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={billingStartDate}
                      onChange={(e) => {
                        setBillingPreset('custom');
                        setBillingStartDate(e.target.value);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={billingEndDate}
                      onChange={(e) => {
                        setBillingPreset('custom');
                        setBillingEndDate(e.target.value);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Quick Presets */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handlePresetChange('last_7_days')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      billingPreset === 'last_7_days'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => handlePresetChange('last_30_days')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      billingPreset === 'last_30_days'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => handlePresetChange('this_month')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      billingPreset === 'this_month'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    This Month
                  </button>
                </div>

                {/* Generate Button */}
                <button
                  onClick={loadBillingReport}
                  disabled={billingLoading}
                  className="w-full md:w-auto px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                >
                  {billingLoading ? 'Generating...' : 'Generate Report'}
                </button>
              </div>
            </div>

            {/* Error Display */}
            {billingError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{billingError}</p>
              </div>
            )}

            {/* Report Results */}
            {billingResult && !billingLoading && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Your Activity Report
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {new Date(billingStartDate).toLocaleDateString()} - {new Date(billingEndDate).toLocaleDateString()}
                </p>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                    <div className="text-purple-600 text-sm font-medium mb-1">Platinum Ads</div>
                    <div className="text-2xl font-bold text-purple-900">{billingResult.totals.adCounts.platinum}</div>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4">
                    <div className="text-yellow-600 text-sm font-medium mb-1">Gold Ads</div>
                    <div className="text-2xl font-bold text-yellow-900">{billingResult.totals.adCounts.gold}</div>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                    <div className="text-gray-600 text-sm font-medium mb-1">Silver Ads</div>
                    <div className="text-2xl font-bold text-gray-900">{billingResult.totals.adCounts.silver}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                    <div className="text-orange-600 text-sm font-medium mb-1">Bronze Ads</div>
                    <div className="text-2xl font-bold text-orange-900">{billingResult.totals.adCounts.bronze}</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                    <div className="text-blue-600 text-sm font-medium mb-1">Magic Link Clicks</div>
                    <div className="text-2xl font-bold text-blue-900">{billingResult.totals.magicLinkClicks}</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                    <div className="text-green-600 text-sm font-medium mb-1">Promo Requests</div>
                    <div className="text-2xl font-bold text-green-900">{billingResult.totals.promoRequests}</div>
                  </div>
                </div>

                {/* Total Summary */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-medium text-gray-700">Total Advertisements Created:</span>
                    <span className="font-bold text-gray-900">
                      {Object.values(billingResult.totals.adCounts).reduce((a, b) => a + b, 0)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {billingLoading && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
                <div className="flex flex-col items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                  <p className="text-gray-600">Generating your report...</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Integration Settings</h3>
                <p className="text-sm text-gray-600 mt-1">Configure your integration preferences and access settings</p>
              </div>
              
              <div className="p-6">
                {/* Get current partner's integration settings */}
                {(() => {
                  const currentPartner = partners.find(p => p.id === user?.partner_id);
                  const integrationSettings = currentPartner?.integration_settings;
                  
                  if (!integrationSettings) {
                    return (
                      <div className="text-center py-8">
                        <Settings className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No Integration Settings</h3>
                        <p className="text-sm text-gray-500">
                          Contact your administrator to configure integration permissions.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-8">
                      {/* Email Notifications */}
                      {integrationSettings.email_notifications && (
                        <EmailNotificationJobsManager 
                          partnerId={user.partner_id || ''}
                          userId={user.id}
                          userName={user.name}
                        />
                      )}

                      {/* API Access */}
                      {integrationSettings.api_access && (
                        <ApiKeysConfig 
                          partnerId={user.partner_id || ''}
                          userId={user.id}
                          userName={user.name}
                        />
                      )}

                      {/* Webhooks */}
                      {integrationSettings.webhook_access && (
                        <WebhooksConfig 
                          partnerId={user.partner_id || ''}
                          userId={user.id}
                          userName={user.name}
                        />
                      )}

                      {/* No integrations enabled */}
                      {!integrationSettings.email_notifications && 
                       !integrationSettings.api_access && 
                       !integrationSettings.webhook_access && (
                        <div className="text-center py-8">
                          <Settings className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No Integrations Enabled</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Contact your administrator to enable integration features.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Advertisement Modal */}
        {(showCreateModal || editingAd) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingAd ? 'Edit Advertisement' : 'Create New Advertisement'}
              </h3>
              
              {/* Warning for editing approved ads */}
              {editingAd && editingAd.approval_status === 'approved' && (
                <div className="mb-4 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
                  <div className="flex items-start">
                    <span className="text-amber-600 mr-3 mt-0.5 text-xl">⚠️</span>
                    <div>
                      <p className="text-sm font-semibold text-amber-800 mb-1">
                        Editing Approved Advertisement
                      </p>
                      <p className="text-sm text-amber-700">
                        This advertisement is currently <strong>approved</strong> and visible in public campaigns. 
                        Any changes you make will reset its status to <strong>pending</strong> and require 
                        re-approval by a global administrator before it appears in campaigns again.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Warning for editing rejected ads */}
              {editingAd && editingAd.approval_status === 'rejected' && (
                <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-0.5 text-xl">ℹ️</span>
                    <div>
                      <p className="text-sm font-semibold text-blue-800 mb-1">
                        Editing Rejected Advertisement
                      </p>
                      <p className="text-sm text-blue-700">
                        This advertisement was previously <strong>rejected</strong>. 
                        Any changes you make will reset its status to <strong>pending</strong> and submit it 
                        for re-review by a global administrator.
                      </p>
                      {editingAd.rejection_reason && (
                        <div className="mt-2 p-2 bg-white rounded border border-blue-200">
                          <p className="text-xs font-semibold text-blue-800">Previous Rejection Reason:</p>
                          <p className="text-xs text-blue-700 mt-1">{editingAd.rejection_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={editingAd ? handleUpdateAd : handleCreateAd} className="space-y-6">
                {/* Advertisement Provider Section */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 size={20} className="text-indigo-600" />
                    <h4 className="text-md font-medium text-gray-900">Advertisement Provider</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Choose who will be displayed as the provider of this advertisement. By default, your partner information is used.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Provider Source</label>
                      <select
                        value={adForm.provider_entity_id}
                        onChange={(e) => handleEntitySelect(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">Use Partner Information (Default)</option>
                        {partnerEntities.length > 0 && (
                          <optgroup label="Select from Entities">
                            {partnerEntities.map(entity => (
                              <option key={entity.id} value={entity.id}>
                                {entity.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        <option value="custom">Custom Provider Info</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        {adForm.provider_entity_id === '' 
                          ? 'Partner name, logo, and privacy policy will be shown to users.'
                          : adForm.provider_entity_id === 'custom'
                          ? 'Enter custom provider details below.'
                          : 'Entity information will be shown to users.'}
                      </p>
                    </div>

                    {/* Custom provider fields - shown when 'custom' is selected */}
                    {adForm.provider_entity_id === 'custom' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-indigo-200">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Provider Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={adForm.provider_name}
                            onChange={(e) => setAdForm(prev => ({ ...prev, provider_name: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="Enter provider name"
                            required={adForm.provider_entity_id === 'custom'}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Provider Logo URL</label>
                          <input
                            type="url"
                            value={adForm.provider_logo_url}
                            onChange={(e) => setAdForm(prev => ({ ...prev, provider_logo_url: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="https://example.com/logo.png"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Provider Privacy Policy URL</label>
                          <input
                            type="url"
                            value={adForm.provider_privacy_policy_url}
                            onChange={(e) => setAdForm(prev => ({ ...prev, provider_privacy_policy_url: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="https://example.com/privacy-policy"
                          />
                        </div>
                      </div>
                    )}

                    {/* Preview of selected entity */}
                    {adForm.provider_entity_id && adForm.provider_entity_id !== 'custom' && adForm.provider_entity_id !== '' && (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-indigo-100">
                        {adForm.provider_logo_url ? (
                          <img 
                            src={adForm.provider_logo_url} 
                            alt={adForm.provider_name}
                            className="w-10 h-10 rounded object-contain bg-gray-50"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                            <Building2 size={20} className="text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{adForm.provider_name}</p>
                          {adForm.provider_privacy_policy_url && (
                            <a 
                              href={adForm.provider_privacy_policy_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:text-indigo-800"
                            >
                              View Privacy Policy →
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Basic Information Section */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Advertisement Name</label>
                      <input
                        type="text"
                        value={adForm.name}
                        onChange={(e) => setAdForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter advertisement name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={adForm.status}
                        onChange={(e) => setAdForm(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={adForm.start_date}
                        onChange={(e) => {
                          setAdForm(prev => ({ ...prev, start_date: e.target.value }));
                          setDateError(''); // Clear error on change
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={adForm.end_date}
                        onChange={(e) => {
                          setAdForm(prev => ({ ...prev, end_date: e.target.value }));
                          setDateError(''); // Clear error on change
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      {dateError && (
                        <p className="text-red-500 text-sm mt-1">{dateError}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                      <select
                        value={adForm.priority}
                        onChange={(e) => setAdForm(prev => ({ ...prev, priority: e.target.value as AdvertisementPriority }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        {/* Show all allocated priorities regardless of quota status */}
                        {/* Quotas only apply to published ads, not ad creation */}
                        {hasPriorityAccess('platinum') && (
                          <option value="platinum">
                            🟣 Platinum ({getRemainingQuota('platinum')} left)
                          </option>
                        )}
                        {hasPriorityAccess('gold') && (
                          <option value="gold">
                            🟡 Gold ({getRemainingQuota('gold')} left)
                          </option>
                        )}
                        {hasPriorityAccess('silver') && (
                          <option value="silver">
                            ⚪ Silver ({getRemainingQuota('silver')} left)
                          </option>
                        )}
                        {hasPriorityAccess('bronze') && (
                          <option value="bronze">
                            🟠 Bronze ({getRemainingQuota('bronze')} left)
                          </option>
                        )}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 You can create ads with any priority. Quotas only apply when ads are published in campaigns.
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Advertisement Mode</label>
                      <select
                        value={adForm.advertisement_mode}
                        onChange={(e) => setAdForm(prev => ({ ...prev, advertisement_mode: e.target.value as 'default' | 'independent' | 'universal' }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="default">Default - Campaign-based only</option>
                        <option value="independent">Independent - Direct link only (counts toward quota when approved)</option>
                        <option value="universal">Universal - Both campaign and direct link</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 <strong>Default:</strong> Traditional workflow (published via campaigns). <strong>Independent:</strong> Accessible via direct link after approval, not in campaigns. <strong>Universal:</strong> Both campaign-based and direct link access.
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Region <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={adForm.region}
                        onChange={(e) => setAdForm(prev => ({ ...prev, region: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select a region</option>
                        {partnerRegions.map((regionCode) => (
                          <option key={regionCode} value={regionCode}>
                            {getCountryNames([regionCode])[0]} ({regionCode})
                          </option>
                        ))}
                      </select>
                      {partnerRegions.length === 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          No regions assigned to your partner. Contact admin to assign regions.
                        </p>
                      )}
                      {partnerRegions.length > 1 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Each advertisement must be assigned to a specific region.
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Description - Full Width */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => applyFormatting('bold')}
                        className="px-3 py-1 text-sm font-semibold rounded border border-gray-200 hover:bg-gray-100"
                        aria-label="Bold"
                      >
                        B
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('italic')}
                        className="px-3 py-1 text-sm italic rounded border border-gray-200 hover:bg-gray-100"
                        aria-label="Italic"
                      >
                        I
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('underline')}
                        className="px-3 py-1 text-sm rounded border border-gray-200 hover:bg-gray-100"
                        aria-label="Underline"
                      >
                        <span className="underline">U</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('bullet')}
                        className="px-3 py-1 text-sm rounded border border-gray-200 hover:bg-gray-100"
                        aria-label="Bullet List"
                        title="Bullet List"
                      >
                        •
                      </button>
                      <button
                        type="button"
                        onClick={() => applyFormatting('number')}
                        className="px-3 py-1 text-sm rounded border border-gray-200 hover:bg-gray-100"
                        aria-label="Numbered List"
                        title="Numbered List"
                      >
                        1.
                      </button>
                    </div>
                    <textarea
                      ref={descriptionRef}
                      value={adForm.description}
                      onChange={(e) => handleDescriptionChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={4}
                      placeholder="Enter advertisement description"
                      required
                    />
                    <div className="flex items-center justify-end mt-2 text-xs text-gray-500">
                      <span>{adForm.description.length}/{DESCRIPTION_MAX_LENGTH}</span>
                    </div>
                  </div>
                  
                  {/* Button Text and Badge Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Button Text
                      </label>
                      <input
                        type="text"
                        value={adForm.button_text}
                        onChange={(e) => setAdForm(prev => ({ ...prev, button_text: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Get the Offer"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave empty to use default: "Get the Offer"
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Badge Text (Optional)
                      </label>
                      <input
                        type="text"
                        value={adForm.badge_text}
                        onChange={(e) => setAdForm(prev => ({ ...prev, badge_text: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="NEW, HOT, 50% OFF, etc."
                        maxLength={20}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Appears in top-right corner of banner (max 20 chars)
                      </p>
                    </div>
                  </div>
                  
                  {/* Badge Color */}
                  {adForm.badge_text && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Badge Color
                      </label>
                      <div className="flex gap-3">
                        {(['red', 'blue', 'green', 'yellow', 'purple', 'orange'] as const).map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setAdForm(prev => ({ ...prev, badge_color: color }))}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                              adForm.badge_color === color
                                ? 'ring-2 ring-offset-2 ring-blue-500 scale-105'
                                : 'hover:scale-105'
                            } ${
                              color === 'red' ? 'bg-red-500 text-white' :
                              color === 'blue' ? 'bg-blue-500 text-white' :
                              color === 'green' ? 'bg-green-500 text-white' :
                              color === 'yellow' ? 'bg-yellow-400 text-gray-900' :
                              color === 'purple' ? 'bg-purple-500 text-white' :
                              'bg-orange-500 text-white'
                            }`}
                          >
                            {color.charAt(0).toUpperCase() + color.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Target URL Section */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="flex items-center cursor-pointer"
                        onClick={() => setAdForm(prev => ({ ...prev, target_url_enabled: !prev.target_url_enabled }))}
                      >
                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                          adForm.target_url_enabled 
                            ? 'bg-blue-600 border-blue-600' 
                            : 'border-gray-300 bg-white'
                        }`}>
                          {adForm.target_url_enabled && (
                            <Check className="text-white" size={14} />
                          )}
                        </div>
                      </div>
                      <label 
                        className="text-sm font-medium text-gray-700 cursor-pointer select-none"
                        onClick={() => setAdForm(prev => ({ ...prev, target_url_enabled: !prev.target_url_enabled }))}
                      >
                        Include link to partner's page
                      </label>
                    </div>
                    
                    {adForm.target_url_enabled && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Target URL
                        </label>
                        <input
                          type="url"
                          value={adForm.target_url}
                          onChange={(e) => setAdForm(prev => ({ ...prev, target_url: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="https://partner-website.com/promo-page"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          <strong>How it works:</strong> After requesting a promocode, users will see a link to this page where they can use the code. 
                          In emails, the ad name will be a clickable link to this URL.
                        </p>
                      </div>
                    )}
                    
                    {!adForm.target_url_enabled && (
                      <p className="text-xs text-gray-500">
                        Enable this to provide a direct link to your website where users can redeem their promocode.
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Banner Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Banner Image</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      {/* File Upload Area */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Upload Banner Image</label>
                        <div
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
                        >
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileSelect(file);
                            }}
                            className="hidden"
                            id="banner-upload"
                          />
                          <label htmlFor="banner-upload" className="cursor-pointer">
                            <div className="flex flex-col items-center">
                              <Image className="h-8 w-8 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-600">
                                <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                JPG, PNG, GIF, WebP up to 10MB
                              </p>
                            </div>
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          <strong>Recommended:</strong> 1920x1080px (16:9 ratio) or 1200x628px for optimal display
                        </p>
                      </div>

                      {/* URL Input Alternative */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Or Enter Image URL</label>
                        <input
                          type="url"
                          value={adForm.banner_file ? '' : adForm.banner_url}
                          onChange={(e) => setAdForm(prev => ({ 
                            ...prev, 
                            banner_url: e.target.value,
                            banner_file: null // Clear file when URL is entered
                          }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="https://example.com/banner.jpg"
                          disabled={!!adForm.banner_file}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Direct URL to your banner image (JPG, PNG, GIF, WebP)
                        </p>
                      </div>
                    </div>
                    
                    {/* Preview */}
                    {adForm.banner_url && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                        
                        {/* Full Advertisement Card Preview */}
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-300">
                          <div className="p-1.5 flex flex-col">
                            {/* Banner with Badge */}
                            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-3 relative">
                              <img
                                src={adForm.banner_url}
                                alt="Banner preview"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  target.nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                              <div className="hidden w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                                <span className="text-gray-400 text-sm">Image failed to load</span>
                              </div>
                              
                              {/* Badge in top-right corner */}
                              {adForm.badge_text && (
                                <div className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                                  adForm.badge_color === 'red' ? 'bg-red-500 text-white' :
                                  adForm.badge_color === 'blue' ? 'bg-blue-500 text-white' :
                                  adForm.badge_color === 'green' ? 'bg-green-500 text-white' :
                                  adForm.badge_color === 'yellow' ? 'bg-yellow-400 text-gray-900' :
                                  adForm.badge_color === 'purple' ? 'bg-purple-500 text-white' :
                                  'bg-orange-500 text-white'
                                }`}>
                                  {adForm.badge_text}
                                </div>
                              )}
                            </div>
                            
                            {/* Advertisement Name */}
                            <h3 className="text-lg font-bold text-gray-900 text-center mb-3 px-1">
                              {adForm.name || 'Advertisement Name'}
                            </h3>
                            
                            {/* Custom Button */}
                            <button
                              type="button"
                              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2.5 px-4 rounded-lg font-medium text-sm cursor-default"
                              onClick={(e) => e.preventDefault()}
                            >
                              {adForm.button_text || 'Get the Offer'}
                            </button>
                          </div>
                        </div>
                        
                        {/* File Info and Actions */}
                        <div className="mt-3 space-y-2">
                          {adForm.banner_file && (
                            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                              <p><strong>File:</strong> {adForm.banner_file.name}</p>
                              <p><strong>Size:</strong> {(adForm.banner_file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          )}
                          {adForm.banner_url && !adForm.banner_file && (
                            <button
                              type="button"
                              onClick={() => setAdForm(prev => ({ ...prev, banner_url: '', banner_file: null }))}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Remove image
                            </button>
                          )}
                          <p className="text-xs text-gray-500 italic">
                            This is how your advertisement will appear to users
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Categories Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Categories</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Categories (up to 3)
                    </label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {availableCategories.map((category) => {
                      const isSelected = adForm.categories.includes(category);
                      const isDisabled = !isSelected && adForm.categories.length >= 3;
                      
                      return (
                        <div
                          key={category}
                          className={`flex items-start p-2 rounded transition-colors ${
                            isSelected
                              ? 'bg-blue-100 text-blue-800'
                              : isDisabled 
                                ? 'opacity-50' 
                                : 'hover:bg-gray-100 cursor-pointer'
                          } ${!isDisabled ? 'cursor-pointer' : ''}`}
                          onClick={() => {
                            if (!isDisabled) {
                              toggleCategory(category);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => {}} // Controlled by parent div click
                            className="mt-1 mr-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 pointer-events-none"
                          />
                          <label className="text-sm cursor-pointer flex-1">
                            {category}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {adForm.categories.length}/3
                  </p>
                  </div>
                </div>

                {/* Promocode Engine Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">🎟️ Promocode Engine</h4>
                  
                  {/* Promocode Type Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Promocode Type
                    </label>
                    <select
                      value={adForm.promocode_config.type}
                      onChange={(e) => setAdForm(prev => ({
                        ...prev,
                        promocode_config: {
                          ...prev.promocode_config,
                          type: e.target.value as any
                        }
                      }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="none">N/A - No Promocode</option>
                      <option value="redirect">N/A - Redirect</option>
                      <option value="generic_static">Generic - Static Code</option>
                      <option value="generic_pool">Generic - Pool (CSV Upload)</option>
                      <option value="personalized_generated">Personalized - Generated</option>
                      <option value="byop">BYOP - Bring Your Own Provider</option>
                    </select>
                  </div>

                  {/* Promocode Delivery Methods */}
                  {adForm.promocode_config.type !== 'none' && adForm.promocode_config.type !== 'redirect' && (
                    <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Promocode Delivery Methods <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={adForm.promocode_delivery_methods.web_display}
                            onChange={(e) => setAdForm(prev => ({
                              ...prev,
                              promocode_delivery_methods: {
                                ...prev.promocode_delivery_methods,
                                web_display: e.target.checked
                              }
                            }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            <strong>Web Display</strong> - Show promocode on screen after request
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={adForm.promocode_delivery_methods.email}
                            onChange={(e) => setAdForm(prev => ({
                              ...prev,
                              promocode_delivery_methods: {
                                ...prev.promocode_delivery_methods,
                                email: e.target.checked
                              }
                            }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            <strong>Email</strong> - Send promocode via email to requester
                          </span>
                        </label>
                      </div>
                      {!adForm.promocode_delivery_methods.web_display && !adForm.promocode_delivery_methods.email && (
                        <p className="text-xs text-red-600 mt-2">
                          ⚠️ At least one delivery method must be selected
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Select how the promocode will be delivered to the requester. You can choose both options.
                      </p>
                    </div>
                  )}

                  {/* Notification for No Promocode */}
                  {adForm.promocode_config.type === 'none' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-blue-800">
                            Direct Contact Option Selected
                          </h4>
                          <div className="mt-2 text-sm text-blue-700">
                            <p>
                              Customers who request this offer will be contacted directly by the advertisement provider via their submitted email address. No promocode will be generated.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* N/A - Redirect */}
                  {adForm.promocode_config.type === 'redirect' && (
                    <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Redirect URL
                      </label>
                      <input
                        type="url"
                        value={adForm.promocode_config.redirect_url || ''}
                        onChange={(e) => setAdForm(prev => ({
                          ...prev,
                          promocode_config: {
                            ...prev.promocode_config,
                            redirect_url: e.target.value
                          }
                        }))}
                        placeholder="https://example.com/offer-page"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-600">
                        Users will be redirected to this URL when they request this offer. No promocode will be generated.
                      </p>
                    </div>
                  )}

                  {/* Generic Static Code */}
                  {adForm.promocode_config.type === 'generic_static' && (
                    <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Static Promocode
                      </label>
                      <input
                        type="text"
                        value={adForm.promocode_config.static_code}
                        onChange={(e) => setAdForm(prev => ({
                          ...prev,
                          promocode_config: {
                            ...prev.promocode_config,
                            static_code: sanitizeToAscii(e.target.value.toUpperCase())
                          }
                        }))}
                        placeholder="e.g., SUMMER2024 (ASCII only)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                      />
                      <p className="text-xs text-gray-600">
                        Same code for all users. Easy to share but less secure.
                      </p>
                    </div>
                  )}

                  {/* Generic Pool */}
                  {adForm.promocode_config.type === 'generic_pool' && (
                    <div className="bg-green-50 p-4 rounded-lg space-y-3">
                      {/* Warning Alert */}
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-yellow-700 font-medium">
                              Important: Auto-Suspension Warning
                            </p>
                            <p className="text-xs text-yellow-600 mt-1">
                              Each code can only be used once. When all codes are used, this advertisement will be <strong>automatically suspended</strong> and removed from public view. Make sure to upload enough codes!
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Promocode Pool (CSV)
                      </label>
                      <input
                        type="file"
                        accept=".csv,.txt"
                        onChange={handleCSVUpload}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                      <div className="flex items-center space-x-4">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-600 mb-1">Usage Limit per Code</label>
                          <input
                            type="number"
                            min="1"
                            value={adForm.promocode_config.pool_usage_limit}
                            onChange={(e) => setAdForm(prev => ({
                              ...prev,
                              promocode_config: {
                                ...prev.promocode_config,
                                pool_usage_limit: parseInt(e.target.value) || 1
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-600 mb-1">Codes Loaded</label>
                          <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-center font-semibold">
                            {adForm.promocode_config.pool_codes?.length || 0}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">
                        Upload a CSV file with one code per line. Each code can be used {adForm.promocode_config.pool_usage_limit} time(s).
                      </p>
                    </div>
                  )}

                  {/* Personalized Generated */}
                  {adForm.promocode_config.type === 'personalized_generated' && (
                    <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Generation Rules</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Code Length</label>
                          <input
                            type="number"
                            min="4"
                            max="20"
                            value={adForm.promocode_config.generation_rules?.length}
                            onChange={(e) => setAdForm(prev => ({
                              ...prev,
                              promocode_config: {
                                ...prev.promocode_config,
                                generation_rules: {
                                  ...prev.promocode_config.generation_rules!,
                                  length: parseInt(e.target.value) || 8
                                }
                              }
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Prefix (optional)</label>
                          <input
                            type="text"
                            maxLength={5}
                            value={adForm.promocode_config.generation_rules?.prefix}
                            onChange={(e) => setAdForm(prev => ({
                              ...prev,
                              promocode_config: {
                                ...prev.promocode_config,
                                generation_rules: {
                                  ...prev.promocode_config.generation_rules!,
                                  prefix: sanitizeToAscii(e.target.value.toUpperCase())
                                }
                              }
                            }))}
                            placeholder="e.g., SAVE (ASCII only)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg uppercase"
                          />
                        </div>
                      </div>
                      <div className="flex space-x-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={adForm.promocode_config.generation_rules?.include_numbers}
                            onChange={(e) => setAdForm(prev => ({
                              ...prev,
                              promocode_config: {
                                ...prev.promocode_config,
                                generation_rules: {
                                  ...prev.promocode_config.generation_rules!,
                                  include_numbers: e.target.checked
                                }
                              }
                            }))}
                            className="rounded border-gray-300 text-purple-600"
                          />
                          <span className="text-sm text-gray-700">Include Numbers</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={adForm.promocode_config.generation_rules?.include_special_chars}
                            onChange={(e) => setAdForm(prev => ({
                              ...prev,
                              promocode_config: {
                                ...prev.promocode_config,
                                generation_rules: {
                                  ...prev.promocode_config.generation_rules!,
                                  include_special_chars: e.target.checked
                                }
                              }
                            }))}
                            className="rounded border-gray-300 text-purple-600"
                          />
                          <span className="text-sm text-gray-700">Include Special Chars</span>
                        </label>
                      </div>
                      <div className="bg-white p-3 rounded border border-purple-200">
                        <p className="text-xs text-gray-600 mb-1">Preview:</p>
                        <p className="font-mono text-sm font-semibold text-purple-700">
                          {adForm.promocode_config.generation_rules?.prefix || ''}
                          {Array(adForm.promocode_config.generation_rules?.length || 8).fill('X').join('')}
                        </p>
                      </div>
                      <p className="text-xs text-gray-600">
                        Unique codes generated for each user based on these rules.
                      </p>
                    </div>
                  )}

                  {/* BYOP - Bring Your Own Provider */}
                  {adForm.promocode_config.type === 'byop' && (
                    <div className="bg-orange-50 p-4 rounded-lg space-y-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">External API Configuration</h5>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">API Endpoint URL <span className="text-red-500">*</span></label>
                        <input
                          type="url"
                          value={adForm.promocode_config.api_config?.endpoint || ''}
                          onChange={(e) => setAdForm(prev => ({
                            ...prev,
                            promocode_config: {
                              ...prev.promocode_config,
                              api_config: {
                                ...prev.promocode_config.api_config,
                                endpoint: e.target.value
                              }
                            }
                          }))}
                          placeholder="https://api.yourprovider.com/generate-code"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">HTTP Method</label>
                          <select
                            value={adForm.promocode_config.api_config?.method || 'POST'}
                            onChange={(e) => {
                              if (adForm.promocode_config.api_config?.api_key && e.target.value === 'GET') {
                                setAdForm(prev => ({
                                  ...prev,
                                  promocode_config: {
                                    ...prev.promocode_config,
                                    api_config: {
                                      ...prev.promocode_config.api_config,
                                      method: 'POST'
                                    }
                                  }
                                }));
                              } else {
                                setAdForm(prev => ({
                                  ...prev,
                                  promocode_config: {
                                    ...prev.promocode_config,
                                    api_config: {
                                      ...prev.promocode_config.api_config,
                                      method: e.target.value as 'GET' | 'POST'
                                    }
                                  }
                                }));
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          >
                            {adForm.promocode_config.api_config?.api_key ? (
                              <option value="POST">POST (required with API key)</option>
                            ) : (
                              <>
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                              </>
                            )}
                          </select>
                          {adForm.promocode_config.api_config?.api_key && adForm.promocode_config.api_config?.method === 'GET' && (
                            <p className="text-xs text-red-600 mt-1">
                              ⚠️ GET method changed to POST because API key requires request body
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">API Key (optional)</label>
                          <input
                            type="password"
                            value={adForm.promocode_config.api_config?.api_key || ''}
                            onChange={(e) => setAdForm(prev => ({
                              ...prev,
                              promocode_config: {
                                ...prev.promocode_config,
                                api_config: {
                                  ...prev.promocode_config.api_config,
                                  api_key: e.target.value
                                }
                              }
                            }))}
                            placeholder="Your API key (sent as Bearer token)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="border-t border-orange-200 pt-4">
                        <h6 className="text-sm font-medium text-gray-700 mb-2">Fallback Configuration</h6>
                        <p className="text-xs text-gray-500 mb-3">Configure what happens if the external API fails to respond</p>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Fallback Type</label>
                            <select
                              value={(adForm.promocode_config.api_config as any)?.fallback?.type || 'none'}
                              onChange={(e) => setAdForm(prev => ({
                                ...prev,
                                promocode_config: {
                                  ...prev.promocode_config,
                                  api_config: {
                                    ...prev.promocode_config.api_config,
                                    fallback: {
                                      ...(prev.promocode_config.api_config as any)?.fallback,
                                      enabled: e.target.value !== 'none',
                                      type: e.target.value === 'none' ? 'static' : e.target.value
                                    }
                                  }
                                }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="none">No fallback (fail if API unavailable)</option>
                              <option value="static">Static fallback code</option>
                              <option value="dynamic">Generate dynamic code</option>
                            </select>
                          </div>
                          {(adForm.promocode_config.api_config as any)?.fallback?.enabled && (adForm.promocode_config.api_config as any)?.fallback?.type === 'static' && (
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Fallback Static Code</label>
                              <input
                                type="text"
                                value={(adForm.promocode_config.api_config as any)?.fallback?.static_code || ''}
                                onChange={(e) => setAdForm(prev => ({
                                  ...prev,
                                  promocode_config: {
                                    ...prev.promocode_config,
                                    api_config: {
                                      ...prev.promocode_config.api_config,
                                      fallback: {
                                        ...(prev.promocode_config.api_config as any)?.fallback,
                                        static_code: sanitizeToAscii(e.target.value.toUpperCase())
                                      }
                                    }
                                  }
                                }))}
                                placeholder="FALLBACK2024 (ASCII only)"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono uppercase"
                              />
                            </div>
                          )}
                          {(adForm.promocode_config.api_config as any)?.fallback?.enabled && (adForm.promocode_config.api_config as any)?.fallback?.type === 'dynamic' && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Code Length</label>
                                <input
                                  type="number"
                                  min={4}
                                  max={20}
                                  value={(adForm.promocode_config.api_config as any)?.fallback?.generation_rules?.length || 10}
                                  onChange={(e) => setAdForm(prev => ({
                                    ...prev,
                                    promocode_config: {
                                      ...prev.promocode_config,
                                      api_config: {
                                        ...prev.promocode_config.api_config,
                                        fallback: {
                                          ...(prev.promocode_config.api_config as any)?.fallback,
                                          generation_rules: {
                                            ...(prev.promocode_config.api_config as any)?.fallback?.generation_rules,
                                            length: parseInt(e.target.value) || 10
                                          }
                                        }
                                      }
                                    }
                                  }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Prefix (optional)</label>
                                <input
                                  type="text"
                                  maxLength={6}
                                  value={(adForm.promocode_config.api_config as any)?.fallback?.generation_rules?.prefix || ''}
                                  onChange={(e) => setAdForm(prev => ({
                                    ...prev,
                                    promocode_config: {
                                      ...prev.promocode_config,
                                      api_config: {
                                        ...prev.promocode_config.api_config,
                                        fallback: {
                                          ...(prev.promocode_config.api_config as any)?.fallback,
                                          generation_rules: {
                                            ...(prev.promocode_config.api_config as any)?.fallback?.generation_rules,
                                            prefix: sanitizeToAscii(e.target.value.toUpperCase())
                                          }
                                        }
                                      }
                                    }
                                  }))}
                                  placeholder="FB (ASCII only)"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono uppercase"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">
                        Integrate with your existing promocode system via API. Configure a fallback to ensure users always receive a code.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingAd(null);
                      resetAdForm();
                      setDateError(''); // Clear date error when closing modal
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200"
                  >
                    {editingAd ? 'Update Advertisement' : 'Create Advertisement'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Entities Tab */}
        {activeTab === 'entities' && user.partner_id && (
          <EntitiesManager partnerId={user.partner_id} />
        )}

        {/* Activity Logs Tab */}
        {activeTab === 'logs' && (
          <ActivityLogsManager 
            partnerId={user.partner_id} 
            userRole="partner_admin"
          />
        )}
      </div>
    </div>
  );
};